import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { useAuth } from '../hooks/useAuth';
import { bookingsService, sickNoteService, shiftsService } from '../services/api';
import { Booking, SickNote as SickNoteType } from '../types';

export function SickNote() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [sickNoteEnabled, setSickNoteEnabled] = useState(false);
  const [showShiftTitle, setShowShiftTitle] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [notes, setNotes] = useState<SickNoteType[]>([]);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [bookingId, setBookingId] = useState<string>('');
  const [plan, setPlan] = useState<'reschedule' | 'leave'>('reschedule');
  const [rescheduleDays, setRescheduleDays] = useState('');
  const [wristbandPhoto, setWristbandPhoto] = useState<string>('');
  const [doctorNotePhoto, setDoctorNotePhoto] = useState<string>('');
  const [wristbandFileName, setWristbandFileName] = useState('');
  const [doctorNoteFileName, setDoctorNoteFileName] = useState('');
  const [requireDoctorNote, setRequireDoctorNote] = useState(false);
  const [reportSickClicked, setReportSickClicked] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancellingNoteId, setCancellingNoteId] = useState<number | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }

    const load = async () => {
      try {
        const [bookingsRes, sickRes, settingsRes] = await Promise.all([
          bookingsService.getUserBookings(),
          sickNoteService.getMine(),
          shiftsService.getSettings(),
        ]);

        setBookings(bookingsRes.data || []);
        setSickNoteEnabled(settingsRes.data?.sick_note_enabled ?? false);
        setShowShiftTitle(settingsRes.data?.show_shift_title ?? true);

        if (sickRes.data?.profile) {
          if (sickRes.data.profile.name) setName(sickRes.data.profile.name);
          if (sickRes.data.profile.phone) setPhone(sickRes.data.profile.phone);
          setRequireDoctorNote(!!sickRes.data.profile.require_doctors_note);
        }

        setNotes(sickRes.data?.notes || []);

        setBookingId('');
        setReportSickClicked(false);
        setError('');
      } catch (err: any) {
        setError('Failed to load your shifts');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [authLoading, user, navigate]);

  const upcomingBookings = bookings.filter((b) => new Date(b.end_time) > new Date());
  const showSickDetails = !!bookingId && reportSickClicked;

  const formatBookingLabel = (b: Booking) => {
    // Times from API are already in configured timezone
    const startParts = b.start_time.substring(0, 16).split('T');
    const endParts = b.end_time.substring(0, 16).split('T');
    const startDate = startParts[0];
    const startTime = startParts[1];
    const endTime = endParts[1];
    const nextDaySuffix = startParts[0] !== endParts[0] ? ' (next day)' : '';
    
    const titlePart = showShiftTitle ? ` · ${b.title || 'Shift'}` : '';
    return `${startDate} ${startTime} – ${endTime}${nextDaySuffix}${titlePart}`;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('Image too large (max 5MB).');
      setWristbandPhoto('');
      setWristbandFileName('');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setWristbandPhoto(reader.result as string);
      setWristbandFileName(file.name);
      setError('');
    };
    reader.readAsDataURL(file);
  };

  const handleDoctorFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Image too large (max 5MB).');
      setDoctorNotePhoto('');
      setDoctorNoteFileName('');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setDoctorNotePhoto(reader.result as string);
      setDoctorNoteFileName(file.name);
      setError('');
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!bookingId) {
      setError('Please select the shift you are missing.');
      return;
    }

    // Check if there's already a sick note for this booking
    const existingNote = notes.find(n => n.booking_id === Number(bookingId));
    if (existingNote) {
      setError('You have already submitted a sick note for this shift.');
      return;
    }

    if (!name.trim() || name.length > 255) {
      setError('Name is required (max 255 characters).');
      return;
    }

    if (!phone.trim()) {
      setError('Phone is required.');
      return;
    }

    if (plan === 'reschedule' && !rescheduleDays.trim()) {
      setError('Please list the days you can take a replacement shift.');
      return;
    }

    if (plan === 'leave' && !wristbandPhoto) {
      setError('Please upload a photo of your cut wristband.');
      return;
    }

    if (requireDoctorNote && !doctorNotePhoto) {
      setError('A doctor note is required before submitting.');
      return;
    }

    try {
      setSubmitting(true);
      await sickNoteService.submitSickNote({
        booking_id: Number(bookingId),
        name: name.trim(),
        phone: phone.trim(),
        plan,
        reschedule_days: plan === 'reschedule' ? rescheduleDays.trim() : undefined,
        wristband_photo: plan === 'leave' ? wristbandPhoto : undefined,
        doctor_note_photo: requireDoctorNote ? doctorNotePhoto : undefined,
      });
      setSuccess('Your sick note has been submitted. We will contact you soon.');
      setRescheduleDays('');
      setWristbandPhoto('');
      setWristbandFileName('');
      setDoctorNotePhoto('');
      setDoctorNoteFileName('');
      const refreshed = await sickNoteService.getMine();
      setNotes(refreshed.data.notes || []);
      // Update require_doctors_note flag in case it was set by the backend
      if (refreshed.data?.profile) {
        setRequireDoctorNote(!!refreshed.data.profile.require_doctors_note);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to submit sick note.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelNote = async () => {
    if (!cancellingNoteId) return;
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      await sickNoteService.cancelSickNote(cancellingNoteId);
      setSuccess('Sick note cancelled successfully.');
      setNotes(notes.filter(n => n.id !== cancellingNoteId));
      setCancelModalOpen(false);
      setCancellingNoteId(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to cancel sick note');
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-700">Loading...</div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <>
      <Header user={user} onLogout={logout} sickNoteEnabled={sickNoteEnabled} />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!sickNoteEnabled ? (
          <div className="bg-white shadow rounded-lg p-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-yellow-900 mb-2">Sick Note Reporting Not Yet Available</h2>
              <p className="text-yellow-800">
                Sick note reporting will be enabled when the festival starts. If you need to report illness before then, please contact us directly.
              </p>
            </div>
          </div>
        ) : (
        <div className="bg-white shadow rounded-lg p-6 space-y-6">
          <div>
            <p className="text-sm uppercase tracking-wide text-orange-700 font-semibold mb-2">Sick Note</p>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Report illness</h1>
            <p className="text-gray-700 mb-4">
              If you are feeling ill and cannot work your shift, you need to let us know well in advance before your shift starts. It is very important that you <strong>submit a report <u>before</u> you leave the festival grounds. </strong>
              Leaving the festival without having submitted a sick note counts as an <strong>unexcused absence</strong>, which may lead to a fine and ban from volunteering in the future.
              </p>
            <p className="text-gray-700 mb-4">
              Fill out this form if you are unable to work your shift due to illness. This is the only way to report your absence.
              After submitting the form, please wait for us to contact you. There is no need to contact us by phone or email.
            </p>
            <p className="text-gray-700 mb-4">
              Please note: We and Roskilde Festival reserve the right to require you to go to the festival’s first aid station for an assessment of whether you're fit to work.
            </p>
            <p className="text-gray-700">
              Need help filling out the form? <a href="https://support.connectingworlds.dk/help/en-us/3-volunteering-101/9-what-happens-if-i-get-sick-and-can-t-complete-my-volunteer-shifts" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                Check out our FAQ: How to report sickness?
              </a>
            </p>
          </div>

          {error && <div className="bg-red-100 text-red-800 px-4 py-3 rounded">{error}</div>}
          {success && <div className="bg-green-100 text-green-800 px-4 py-3 rounded">{success}</div>}

          {notes.length > 0 && (
            <div className="pt-1 pb-4">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Your sick requests</h2>
              <div className="space-y-3">
                {notes.map((n) => {
                  const originalStart = n.original_shift_start_time ? new Date(n.original_shift_start_time) : null;
                  const originalEnd = n.original_shift_end_time ? new Date(n.original_shift_end_time) : null;
                  const newStart = n.new_shift_start_time ? new Date(n.new_shift_start_time) : null;
                  const newEnd = n.new_shift_end_time ? new Date(n.new_shift_end_time) : null;
                  return (
                    <div key={n.id} className="border rounded-lg p-3 bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">
                            {n.status === 'reschedule_accepted' && n.new_shift_title ? (
                              <>
                                {showShiftTitle ? (n.original_shift_title || 'Shift') : 'Shift'} {originalStart ? `(${originalStart.toLocaleString('en-GB', { hour12: false })})` : ''} → {showShiftTitle ? n.new_shift_title : 'Shift'}
                                {newStart ? ` (${newStart.toLocaleString('en-GB', { hour12: false })})` : ''}
                              </>
                            ) : (
                              <>
                                {showShiftTitle ? (n.original_shift_title || 'Shift') : 'Shift'}{originalStart ? ` · ${originalStart.toLocaleString('en-GB', { hour12: false })}` : ''}
                              </>
                            )}
                          </p>
                          <p className="text-sm text-gray-700">Plan: {n.plan === 'reschedule' ? 'Reschedule' : 'Leave festival'}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-semibold px-2 py-1 rounded ${
                            n.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            n.status === 'reschedule_accepted' ? 'bg-green-100 text-green-800' :
                            n.status === 'doctor_required' ? 'bg-blue-100 text-blue-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {n.status.replace('_', ' ')}
                          </span>
                          {n.status === 'pending' && (
                            <button
                              onClick={() => {
                                setCancellingNoteId(n.id);
                                setCancelModalOpen(true);
                              }}
                              className="text-xs text-red-600 hover:text-red-800 font-medium px-2 py-1 border border-red-200 rounded hover:bg-red-50"
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </div>
                      {n.admin_notes && <p className="text-sm text-gray-700 mt-2">Note: {n.admin_notes}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={255}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Your full name"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">{name.length}/255 maximum characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  placeholder="We will contact you"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">When is your shift? *</label>
                {upcomingBookings.length === 0 ? (
                  <p className="text-sm text-gray-600">You have no upcoming shifts. If this is unexpected, please contact us.</p>
                ) : (
                  <select
                    value={bookingId}
                    onChange={(e) => {
                      setBookingId(e.target.value);
                      setReportSickClicked(false);
                      setDoctorNotePhoto('');
                      setDoctorNoteFileName('');
                      setWristbandPhoto('');
                      setWristbandFileName('');
                    }}
                    className="w-full border rounded px-3 py-2"
                    required
                  >
                    <option value="" disabled>Select Shift</option>
                    {upcomingBookings
                      .slice()
                      .sort((a, b) => a.start_time.localeCompare(b.start_time))
                      .map((b) => {
                      const hasSickNote = notes.some(n => n.booking_id === b.id);
                      return (
                        <option key={b.id} value={b.id} disabled={hasSickNote} className={hasSickNote ? 'text-gray-400' : ''}>
                          {formatBookingLabel(b)}{hasSickNote ? ' (sick note already submitted)' : ''}
                        </option>
                      );
                    })}
                  </select>
                )}
              </div>
            </div>

            {showSickDetails && requireDoctorNote && (
              <div className="space-y-2">
                <div className="bg-orange-50 border-l-4 border-orange-500 text-orange-900 px-4 py-3 rounded">
                  <p className="text-sm font-semibold">Doctor note required.</p>
                  <p className="text-xs">Get assessed at the festival first aid station and upload a clear photo of the doctor note they give you.</p>
                </div>
                <p className="text-sm font-medium text-gray-700">Doctor note upload *</p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleDoctorFileChange}
                  className="block"
                />
                {doctorNoteFileName && <p className="text-xs text-gray-600">Uploaded: {doctorNoteFileName}</p>}
              </div>
            )}

            {showSickDetails && (!requireDoctorNote || doctorNotePhoto) && (
              <>
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-700">Please select *</p>
                  <p className="text-xs text-gray-600">These are the only two options: either you reschedule your shift and stay, or you leave the festival completely.</p>
                  <div className="space-y-2">
                    <label className="flex items-start gap-2">
                      <input
                        type="radio"
                        name="plan"
                        value="reschedule"
                        checked={plan === 'reschedule'}
                        onChange={() => setPlan('reschedule')}
                        className="mt-1"
                      />
                      <span>
                        <span className="font-medium text-gray-900">I expect to recover soon, please assign me a new shift as replacement.</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-2">
                      <input
                        type="radio"
                        name="plan"
                        value="leave"
                        checked={plan === 'leave'}
                        onChange={() => setPlan('leave')}
                        className="mt-1"
                      />
                      <span>
                        <span className="font-medium text-gray-900">I don't think I'll recover quickly, I want to leave the festival completely.</span>
                      </span>
                    </label>
                  </div>
                </div>

                {plan === 'reschedule' && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">When can we reschedule your shift for? Please list all the days possible. *</label>
                    <textarea
                      value={rescheduleDays}
                      onChange={(e) => setRescheduleDays(e.target.value)}
                      className="w-full border rounded px-3 py-2 min-h-[100px]"
                      placeholder="Example: Thursday evening, Friday morning"
                      required
                    />
                    <p className="text-xs text-gray-600">We cannot guarantee a specific day. We will contact you with a new shift.</p>
                  </div>
                )}

                {plan === 'leave' && (
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-gray-700"><strong>You need to leave the festival and cut your wristband.</strong> Do not cut your wristband until you are outside.</p>
                    {/* Follow these steps: *</p> */}
                    <p className="text-sm text-gray-600">After cutting your wristband, please upload a clear photo of the inside of the chip showing the numbers. We need this to verify that you have left the festival.</p>
                    {/* <ol className="space-y-2 text-sm text-gray-700 list-none">
                      <li className="flex gap-2"><span className="font-bold text-gray-900 shrink-0">1.</span><span>Go to <strong>Gate 35</strong> — the gate where you work your shifts. (<a href="https://maps.app.goo.gl/EpWYDvZmkDKsWjL89" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Google Maps</a>)</span></li>
                      <li className="flex gap-2"><span className="font-bold text-gray-900 shrink-0">2.</span><span>Find the current supervisor on duty and let them know you are leaving.</span></li>
                      <li className="flex gap-2"><span className="font-bold text-gray-900 shrink-0">3.</span><span>Exit the festival grounds together with the supervisor. <strong>Do not cut your wristband until you are outside.</strong></span></li>
                      <li className="flex gap-2"><span className="font-bold text-gray-900 shrink-0">4.</span><span>Cut your wristband and take a photo clearly showing the numbers on the inside of the chip.</span></li>
                      <li className="flex gap-2"><span className="font-bold text-gray-900 shrink-0">5.</span><span>Hand the wristband to the supervisor.</span></li>
                      <li className="flex gap-2"><span className="font-bold text-gray-900 shrink-0">6.</span><span>Upload your photo below.</span></li>
                    </ol> */}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="block"
                    />
                    {wristbandFileName && (
                      <p className="text-xs text-gray-600">Uploaded: {wristbandFileName}</p>
                    )}
                  </div>
                )}
              </>
            )}

            {bookingId && !reportSickClicked && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setReportSickClicked(true)}
                  className="px-6 py-2 rounded font-semibold text-white bg-blue-600 hover:bg-blue-700"
                >
                  Report sickness
                </button>
              </div>
            )}

            {showSickDetails && (
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={submitting || (upcomingBookings.length === 0)}
                  className={`px-6 py-2 rounded font-semibold text-white ${submitting ? 'bg-gray-500' : 'bg-blue-600 hover:bg-blue-700'} disabled:bg-gray-400`}
                >
                  {submitting ? 'Submitting...' : 'Submit sick note'}
                </button>
              </div>
            )}
          </form>
        </div>
        )}
      </main>

      {/* Cancel Confirmation Modal */}
      {cancelModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Cancel Sick Note?</h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to cancel this sick note request? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setCancelModalOpen(false);
                  setCancellingNoteId(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                disabled={submitting}
              >
                No, keep it
              </button>
              <button
                onClick={handleCancelNote}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-medium"
                disabled={submitting}
              >
                {submitting ? 'Cancelling...' : 'Yes, cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
